import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { initializeParameter, updateParameter } from '../../Components/CodeEditor/Devices/DeviceCardBuilder';
import { DeviceCardProps } from '../../Components/CodeEditor/Devices/DeviceCardProps';
import { applicationSlice } from '../../redux/ApplicationSlice';
import { ApplicationActions } from '../../utils/constants';

const { CODE_VALUE_OUT_OF_RANGE } = ApplicationActions.Dialogs;

export interface UseControlInitializationOptions<T> {
  /** Minimum allowed value (for validation) */
  min?: number;
  /** Maximum allowed value (for validation) */
  max?: number;
  /** Function to parse the raw string value from code */
  parseValue?: (value: string) => T;
  /** Identifies raw code values that should be preserved and shown as custom code instead of treated as normal editable state. */
  isUnsupportedRawValue?: (rawValue: unknown, parsedValue: T) => boolean;
  /** Function to validate the parsed value */
  validateValue?: (value: T) => boolean;
  /** Custom validation error handler */
  onValidationError?: (value: T) => void;
}

type DeviceCardPropsWithOptionalLabel = DeviceCardProps & {
  label?: unknown;
};

/**
 * Custom hook to handle initialization and editor synchronization for device configuration controls.
 *
 * Eliminates duplicate code across UIControls by centralizing:
 * - Parameter initialization from code
 * - Editor content change subscriptions
 * - Value validation and range checking
 * - Disposal cleanup
 *
 * @param props - Device card props containing editor, header, parameterName, etc.
 * @param options - Validation options (min, max, custom parse/validate functions)
 * @returns Object with current value, setValue function, and initialize function
 *
 * @example
 * ```tsx
 * const { value, setValue } = useControlInitialization(props, {
 *   min: 0,
 *   max: 100,
 *   parseValue: (v) => Number.parseFloat(v),
 * });
 * ```
 */
export const useControlInitialization = <T = unknown>(
  props: DeviceCardProps,
  options: UseControlInitializationOptions<T> = {}
): {
  value: T;
  setValue: (value: T) => void;
  initialize: () => void;
  unsupportedRawValue?: string;
  isUnset: boolean;
} => {
  const { min, max, parseValue, isUnsupportedRawValue, validateValue, onValidationError } = options;
  // Initialize with a default value to avoid uncontrolled -> controlled transition
  // For numbers, use 0 or NaN; for other types, we'll handle in initialize()
  const getDefaultValue = (): T => {
    if (typeof min === 'number') {
      return min as unknown as T;
    }
    return undefined as T;
  };
  const [value, setInitializedValue] = useState<T>(getDefaultValue());
  const [isUnset, setIsUnset] = useState(true);
  const [unsupportedRawValue, setUnsupportedRawValue] = useState<string | undefined>();
  const dispatch = useDispatch();

  const setValue = useCallback((nextValue: T) => {
    setUnsupportedRawValue(undefined);
    setIsUnset(false);
    setInitializedValue(nextValue);
  }, []);

  const initialize = useCallback(() => {
    const rawValue = initializeParameter(props);
    if (rawValue == null || (typeof rawValue === 'string' && rawValue.trim() === '')) {
      setUnsupportedRawValue(undefined);
      setIsUnset(true);
      setInitializedValue((typeof min === 'number' ? min : undefined) as T);
      return;
    }
    const rawValueString = String(rawValue);

    const labelCandidate = (props as DeviceCardPropsWithOptionalLabel).label;
    const serializableLabel = typeof labelCandidate === 'string' || typeof labelCandidate === 'number' ? String(labelCandidate) : undefined;

    // Parse value (default to parseFloat for numbers)
    let parsed = parseValue ? parseValue(rawValueString) : (Number.parseFloat(rawValueString) as T);

    if (isUnsupportedRawValue?.(rawValue, parsed)) {
      setUnsupportedRawValue(rawValueString.trim());
      setIsUnset(false);
      setInitializedValue(parsed);
      return;
    }
    setUnsupportedRawValue(undefined);

    // Range validation
    if (typeof min === 'number' && typeof max === 'number' && typeof parsed === 'number') {
      if (parsed < min) {
        dispatch(
          applicationSlice.actions.onOpenDialog({
            dialog: CODE_VALUE_OUT_OF_RANGE.dialog,
            options: { header: props.header, label: serializableLabel, min, max },
          })
        );
        parsed = min as unknown as T;
        updateParameter(props, String(min));
      } else if (parsed > max) {
        dispatch(
          applicationSlice.actions.onOpenDialog({
            dialog: CODE_VALUE_OUT_OF_RANGE.dialog,
            options: { header: props.header, label: serializableLabel, min, max },
          })
        );
        parsed = max as unknown as T;
        updateParameter(props, String(max));
      }
    }

    // Custom validation
    if (validateValue && !validateValue(parsed)) {
      if (onValidationError) {
        onValidationError(parsed);
      }
      return;
    }

    setInitializedValue(parsed);
    setIsUnset(false);
  }, [props, dispatch, min, max, parseValue, isUnsupportedRawValue, validateValue, onValidationError]);

  // Subscribe to editor content changes
  useEffect(() => {
    const ed = props.editor ?? window.__bps_editor ?? undefined;
    initialize();

    const disposable = ed?.onDidChangeModelContent(() => {
      initialize();
    });

    return () => {
      try {
        disposable?.dispose();
      } catch (error) {
        // Ignore disposal errors - editor might already be disposed
        console.warn('Failed to dispose editor subscription:', error);
      }
    };
  }, [props.editor, initialize]);

  return { value, setValue, initialize, unsupportedRawValue, isUnset };
};

export default useControlInitialization;
